import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Loader2, Mail, Lock, User, Key, Gift } from 'lucide-react';
import { toast } from 'sonner';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';

import { BACKEND_URL, API } from './config';

// Phase R1: read ?ref=<code> from the URL on mount and persist it in localStorage
// so it survives Google-OAuth round-trips and across tabs for ~30 days.
const REFERRAL_LOCALSTORAGE_KEY = 'onlymass_referral_code';
const REFERRAL_LOCALSTORAGE_EXPIRY_DAYS = 30;

function _readReferralFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('ref') || '').trim();
    if (code && code.length <= 64 && /^[A-Za-z0-9_-]+$/.test(code)) return code;
  } catch (e) { /* noop */ }
  return null;
}

function _readReferralFromStorage() {
  try {
    const raw = localStorage.getItem(REFERRAL_LOCALSTORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.code && parsed.expires_at && Date.now() < parsed.expires_at) {
      return parsed.code;
    }
  } catch (e) { /* noop */ }
  return null;
}

function _persistReferral(code) {
  try {
    localStorage.setItem(REFERRAL_LOCALSTORAGE_KEY, JSON.stringify({
      code,
      expires_at: Date.now() + REFERRAL_LOCALSTORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    }));
  } catch (e) { /* noop */ }
}

function _clearReferral() {
  try { localStorage.removeItem(REFERRAL_LOCALSTORAGE_KEY); } catch (e) { /* noop */ }
}

export const AuthModal = ({ open, onClose, defaultMode = 'login', onLoginSuccess }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultMode);
  const [loading, setLoading] = useState(false);

  // Referral attribution — captured from ?ref=<code> on mount or from localStorage
  const [referralCode, setReferralCode] = useState(null);
  const [inviterName, setInviterName] = useState(null);

  useEffect(() => {
    const fromUrl = _readReferralFromUrl();
    const fromStorage = _readReferralFromStorage();
    const code = fromUrl || fromStorage;
    if (!code) return;
    if (fromUrl) _persistReferral(fromUrl);
    setReferralCode(code);
    // When a referral code is present, default to the signup tab since the
    // inviter wants the visitor to create an account, not log in.
    setActiveTab('signup');
    // Lookup the inviter's display name for the banner copy. Silently degrade
    // if the code is invalid — signup must still work without the banner.
    axios.get(`${API}/referrals/lookup/${encodeURIComponent(code)}`)
      .then(({ data }) => setInviterName(data?.inviter_name || null))
      .catch(() => setInviterName(null));
  }, []);

  // Google OAuth handler
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // The redirect must be derived from window.location.origin at call time.
  // Phase R1 (2026-06-25): if a referral code is present in localStorage
  // (captured earlier from `?ref=<code>`), append it to the redirect target
  // so it survives the Emergent OAuth round-trip. Emergent Auth appends
  // `#session_id=...` to whatever we pass in `redirect=...`, so the final
  // landing URL becomes `<origin>/dashboard?ref=<code>#session_id=<id>`.
  const handleGoogleSignIn = () => {
    let redirectUrl = `${window.location.origin}/dashboard`;
    const refForRoundTrip = referralCode || _readReferralFromStorage();
    if (refForRoundTrip) {
      redirectUrl = `${redirectUrl}?ref=${encodeURIComponent(refForRoundTrip)}`;
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  // Signup form state
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  // Password reset state
  const [resetStep, setResetStep] = useState(1); // 1 = request, 2 = confirm
  const [resetData, setResetData] = useState({
    email: '',
    reset_code: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginData.email,
        password: loginData.password
      });

      // Store session token and user data with proper cookie attributes
      const cookieOptions = { 
        expires: 7,
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:',
        path: '/'
      };
      Cookies.set('session_token', response.data.session_token, cookieOptions);
      Cookies.set('user_data', JSON.stringify(response.data.user), cookieOptions);
      
      toast.success('Login successful!');
      
      // Call the onLoginSuccess callback if provided
      if (onLoginSuccess) {
        onLoginSuccess(response.data.user, response.data.session_token);
      }
      
      // Close modal and navigate
      onClose();
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Signup handler
  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!signupData.name || !signupData.email || !signupData.password || !signupData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: signupData.name,
        email: signupData.email,
        password: signupData.password,
      };
      if (referralCode) payload.referral_code = referralCode;
      const response = await axios.post(`${API}/auth/register`, payload);

      // Store session token and user data with proper cookie attributes
      const cookieOptions = { 
        expires: 7,
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:',
        path: '/'
      };
      Cookies.set('session_token', response.data.session_token, cookieOptions);
      Cookies.set('user_data', JSON.stringify(response.data.user), cookieOptions);
      
      // Phase R1: clear stored referral code after successful signup
      _clearReferral();
      toast.success('Account created successfully!');
      
      // Call the onLoginSuccess callback if provided
      if (onLoginSuccess) {
        onLoginSuccess(response.data.user, response.data.session_token);
      }
      
      // Close modal and navigate
      onClose();
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Password reset request handler
  const handlePasswordResetRequest = async (e) => {
    e.preventDefault();
    
    if (!resetData.email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, {
        email: resetData.email
      });

      toast.success('If your email is on file, a reset code has been sent. Please check your inbox.');
      setResetStep(2);

    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send reset code';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Password reset confirmation handler
  const handlePasswordResetConfirm = async (e) => {
    e.preventDefault();
    
    if (!resetData.reset_code || !resetData.new_password || !resetData.confirm_password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (resetData.new_password !== resetData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (resetData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetData.email,
        reset_code: resetData.reset_code,
        new_password: resetData.new_password
      });
      
      toast.success('Password reset successful! Please login with your new password.');
      
      // Reset state and go back to login
      setShowPasswordReset(false);
      setResetStep(1);
      setResetData({ email: '', reset_code: '', new_password: '', confirm_password: '' });
      setActiveTab('login');
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Password reset failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetInputChange = (e) => {
    setResetData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (showPasswordReset) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Reset Password</DialogTitle>
            <DialogDescription>
              {resetStep === 1 
                ? 'Enter your email address to receive a reset code'
                : 'Enter the reset code and your new password'
              }
            </DialogDescription>
          </DialogHeader>

          {resetStep === 1 ? (
            <form onSubmit={handlePasswordResetRequest} className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    name="email"
                    placeholder="your@email.com"
                    value={resetData.email}
                    onChange={handleResetInputChange}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowPasswordReset(false)}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Code'
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordResetConfirm} className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Reset Code</label>
                <div className="relative">
                  <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    name="reset_code"
                    placeholder="Enter 6-digit code"
                    value={resetData.reset_code}
                    onChange={handleResetInputChange}
                    className="pl-10"
                    maxLength={6}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    name="new_password"
                    placeholder="Enter new password"
                    value={resetData.new_password}
                    onChange={handleResetInputChange}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    name="confirm_password"
                    placeholder="Confirm new password"
                    value={resetData.confirm_password}
                    onChange={handleResetInputChange}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setResetStep(1);
                    setResetData({ ...resetData, reset_code: '', new_password: '', confirm_password: '' });
                  }}
                  disabled={loading}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center mb-2">
            <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-12 h-12 rounded-xl" />
          </div>
          <DialogTitle className="text-2xl font-bold text-center">Welcome to OnlyMass</DialogTitle>
          <DialogDescription className="text-center">
            Sign in to your account or create a new one
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          {/* Login Tab */}
          <TabsContent value="login" className="space-y-4">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button 
                type="button"
                variant="link" 
                className="text-sm text-blue-600 p-0 h-auto"
                onClick={() => {
                  setResetData({ ...resetData, email: loginData.email });
                  setShowPasswordReset(true);
                }}
              >
                Forgot password?
              </Button>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>

              {/* OR Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">Or continue with</span>
                </div>
              </div>

              {/* Google Sign-In Button */}
              <Button 
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </Button>
            </form>
          </TabsContent>

          {/* Signup Tab */}
          <TabsContent value="signup" className="space-y-4">
            {/* Phase R1: referral banner — shown only when ?ref=<code> is present
                and the code resolves to a known inviter. Silent no-op otherwise. */}
            {referralCode && inviterName && (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start gap-2"
                data-testid="referral-banner"
              >
                <Gift className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                <div>
                  <p className="font-semibold" data-testid="referral-banner-headline">
                    You&apos;re joining OnlyMass through an invite from {inviterName}.
                  </p>
                  <p className="text-emerald-800 mt-1 text-xs">
                    Create your account and start with the standard creator launch rate:
                    15% platform fee for your first 3 months if you become a creator.
                  </p>
                </div>
              </div>
            )}
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Your full name"
                    value={signupData.name}
                    onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    placeholder="Create a password (min 6 characters)"
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="password"
                    placeholder="Confirm your password"
                    value={signupData.confirmPassword}
                    onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>

              {/* OR Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">Or continue with</span>
                </div>
              </div>

              {/* Google Sign-In Button */}
              <Button 
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign up with Google
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
